import { useCallback } from 'react'
import {
  useIsAuthenticated,
  useMsal,
  useAccount,
} from '@azure/msal-react'
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser'
import { apiScopes, loginRequest, clearStaleInteractionStatus } from '@/features/auth/lib/msalConfig'
import { clearAuthProfileCache } from '@/features/auth/hooks/useAuthz'

export interface AuthUser {
  name: string | undefined
  email: string | undefined
  account: AccountInfo
}

/**
 * Central auth hook — wraps MSAL with convenience methods for the portal.
 */
export function useAuth() {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()

  // Pick the first account in cache (or the active account if set)
  const account = useAccount(instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null)

  const user: AuthUser | null = account
    ? {
        name: account.name,
        email: account.username,
        account,
      }
    : null

  /**
   * Silently acquire an access token for the IR API.
   * Falls back to an interactive popup if silent acquisition fails (e.g. consent needed).
   */
  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account')

    try {
      const result = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account,
      })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({
          scopes: apiScopes,
          account,
        })
        return result.accessToken
      }
      throw err
    }
  }, [instance, account])

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest).catch((err) => {
      // A stale lock from an interrupted redirect blocks all sign-in attempts;
      // clear it and retry once so the user isn't stuck on a dead button.
      if (err instanceof BrowserAuthError && err.errorCode === 'interaction_in_progress') {
        clearStaleInteractionStatus()
        instance.loginRedirect(loginRequest).catch((retryErr) => {
          console.error('[auth] loginRedirect failed after retry:', retryErr)
        })
        return
      }
      console.error('[auth] loginRedirect failed:', err)
    })
  }, [instance])

  const logout = useCallback(() => {
    clearAuthProfileCache()
    instance
      .logoutRedirect({
        account: account ?? undefined,
        postLogoutRedirectUri: window.location.origin,
      })
      .catch((err) => {
        console.error('[auth] logoutRedirect failed:', err)
      })
  }, [instance, account])

  return {
    isAuthenticated,
    inProgress,
    user,
    login,
    logout,
    getAccessToken,
  }
}
